import React from 'react';
import { Box, Typography } from '@mui/material';

// ── תפילה אחרי ספירה — Kabbalah ─────────────────────────────────────────────
const AfterCountSection = ({ section }) => (
  <Box sx={{ mt: 1.5, width: '100%', maxWidth: 380 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
      <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.5))' }} />
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.58rem',
        color: 'rgba(201,168,76,0.7)',
        letterSpacing: '0.1em',
        whiteSpace: 'nowrap',
      }}>
        ✦ {section.title} ✦
      </Typography>
      <Box sx={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(201,168,76,0.5), transparent)' }} />
    </Box>
    {section.lines.map((line) => (
      <Typography key={line.id} sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: '0.6rem',
        color: 'rgba(220,210,255,0.8)',
        lineHeight: 1.85,
        textAlign: 'center',
        direction: 'rtl',
      }}>
        {line.text}
      </Typography>
    ))}
  </Box>
);

// ── כוכבי קישוט ──────────────────────────────────────────────────────────────
const StarDot = ({ size = 4, opacity = 0.4, top, left, right, bottom }) => (
  <Box sx={{
    position: 'absolute',
    width: size, height: size,
    borderRadius: '50%',
    background: '#c9a84c',
    opacity,
    top, left, right, bottom,
  }} />
);

// ── תבנית קבלה ───────────────────────────────────────────────────────────────
const Kabbalah = ({
  omerDay, isOmerSeason,
  isPreview, previewCountdown,
  mainCountText, subCountText, omerDayHebrew, sefirahName,
  showBracha, brachaText,
  showLeshemYichud, leshemYichudLines,
  showPasuk, pasukText,
  showAfterCount, afterCountSections,
  offSeasonMessage,
  title, showTitle,
  bgColor,
}) => {
  if (!isOmerSeason) {
    return (
      <Box sx={{
        background: bgColor || 'linear-gradient(160deg, #1a0a2e 0%, #0d1b4e 100%)',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        direction: 'rtl',
      }}>
        <Typography sx={{ color: 'rgba(201,168,76,0.4)', fontFamily: 'Frank Ruhl Libre, serif', fontSize: '0.9rem' }}>
          {offSeasonMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      background: bgColor || 'linear-gradient(160deg, #1a0a2e 0%, #0d1b4e 100%)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: '14px 16px',
      direction: 'rtl',
      position: 'relative',
      overflowY: 'auto',
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: 'rgba(201,168,76,0.3)', borderRadius: 2 },
    }}>
      {/* קישוטי כוכבים */}
      <StarDot top={12} left={16} opacity={0.25} />
      <StarDot top={24} left={8}  size={3} opacity={0.2} />
      <StarDot top={12} right={16} opacity={0.25} />
      <StarDot top={24} right={8}  size={3} opacity={0.2} />

      {/* קו עיטור עליון */}
      <Box sx={{
        width: '75%',
        height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.6), transparent)',
        mb: 1.5,
      }} />

      {showTitle && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: '0.65rem',
          color: 'rgba(201,168,76,0.6)',
          letterSpacing: '0.15em',
          mb: 1,
        }}>
          {title}
        </Typography>
      )}

      {/* באנר ספירה לאחור */}
      {isPreview && previewCountdown && (
        <Box sx={{
          mb: 1.5, px: 2, py: 0.75,
          background: 'rgba(201,168,76,0.08)',
          border: '1px solid rgba(201,168,76,0.25)',
          borderRadius: 1,
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.72rem',
            color: '#c9a84c',
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {previewCountdown}
          </Typography>
        </Box>
      )}

      {/* לשם ייחוד */}
      {showLeshemYichud && (
        <Box sx={{
          mb: 1.5, px: 2, py: 0.75,
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 1,
          width: '100%',
          maxWidth: 380,
        }}>
          {leshemYichudLines.map((line, i) => (
            <Typography key={i} sx={{
              fontFamily: 'Frank Ruhl Libre, serif',
              fontSize: '0.57rem',
              color: 'rgba(220,210,255,0.6)',
              lineHeight: 1.9,
              textAlign: 'center',
              direction: 'rtl',
            }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}

      {/* פסוק */}
      {showPasuk !== 'none' && pasukText && (
        <Box sx={{ mb: 1.5, px: 1 }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.6rem',
            color: 'rgba(201,168,76,0.7)',
            lineHeight: 2,
            textAlign: 'center',
            fontStyle: 'italic',
            direction: 'rtl',
          }}>
            {pasukText}
          </Typography>
        </Box>
      )}

      {/* ברכה */}
      {showBracha && (
        <Box sx={{
          mb: 1.5, px: 2, py: 1,
          background: 'rgba(201,168,76,0.06)',
          borderRadius: 1,
          width: '100%',
          maxWidth: 380,
          border: '1px solid rgba(201,168,76,0.15)',
        }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, serif',
            fontSize: '0.7rem',
            color: 'rgba(240,230,255,0.9)',
            lineHeight: 2,
            textAlign: 'center',
            direction: 'rtl',
          }}>
            {brachaText}
          </Typography>
        </Box>
      )}

      {/* שם הספירה — הבולט ביותר */}
      <Box sx={{
        mb: 0.5,
        px: 2, py: 0.75,
        border: '1px solid rgba(201,168,76,0.35)',
        borderRadius: '50px',
        background: 'rgba(201,168,76,0.08)',
      }}>
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: 'clamp(1rem, 3.5vw, 1.4rem)',
          color: '#c9a84c',
          textAlign: 'center',
          letterSpacing: '0.05em',
        }}>
          {sefirahName}
        </Typography>
      </Box>

      {/* מספר */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(3rem, 11vw, 5.5rem)',
        fontWeight: 700,
        color: '#c9a84c',
        lineHeight: 1.05,
        textShadow: '0 0 40px rgba(201,168,76,0.35)',
      }}>
        {omerDayHebrew}
      </Typography>

      {/* שורת ספירה */}
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, serif',
        fontSize: 'clamp(0.85rem, 2.8vw, 1.2rem)',
        color: 'rgba(240,230,255,0.9)',
        mt: 0.5,
        textAlign: 'center',
        direction: 'rtl',
      }}>
        {mainCountText}
      </Typography>

      {subCountText && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, serif',
          fontSize: 'clamp(0.7rem, 2vw, 0.9rem)',
          color: 'rgba(201,168,76,0.75)',
          mt: 0.35,
          textAlign: 'center',
          direction: 'rtl',
        }}>
          {subCountText}
        </Typography>
      )}

      {/* תפילות אחרי ספירה */}
      {showAfterCount && afterCountSections.length > 0 && (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 0.5 }}>
          {afterCountSections.map((section) => (
            <AfterCountSection key={section.id} section={section} />
          ))}
        </Box>
      )}

      {/* קו תחתון */}
      <Box sx={{
        width: '75%',
        height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.6), transparent)',
        mt: 'auto',
        pt: 1.5,
      }} />
    </Box>
  );
};

export default Kabbalah;
